import type { FC } from "react";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { MultiIncludeExcludeKeywordsInline } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/MultiIncludeExcludeKeywordsInline";
import { MultiIncludeExcludeWebsitesInline } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/MultiIncludeExcludeWebsitesInline";
import type { IRule } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import type { EChangeFilterValues } from "app/components/filters-bar/change-filter/ChangeFilter";
import { changeFilterItems } from "app/common/keyword-research/landing-pages/landingPagesFilterConsts";
import Sentry from "@similarweb/sentry-logger";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { capitalize } from "lodash";

const SOURCE_TYPE = "sourceType";
export const sourceTypes = [
    {
        id: sourceTypeMap.Organic,
        text: capitalize(sourceTypeMap.Organic),
    },
    {
        id: sourceTypeMap.Paid,
        text: capitalize(sourceTypeMap.Paid),
    },
];

const getChangeFilterItemsWithClear = () => [
    {
        id: undefined as EChangeFilterValues,
        text: translate("home.dashboards.wizard.filters.clicksChange.empty"),
    },
    ...changeFilterItems,
];

const orderByOptions = [
    { id: "Clicks desc", text: "Traffic Share" },
    { id: "ClicksChange desc", text: "Change" },
];

interface ITableLandingPageDashboardWidgetFiltersProps {
    widgetFilters: any;
    changeFilter: (filters: any) => void;
}

export const TableLandingPageDashboardWidgetFilters: FC<ITableLandingPageDashboardWidgetFiltersProps> = ({
    widgetFilters,
    changeFilter,
}) => {
    let selectedKeywords: IRule[] = [];
    let selectedWebsites: IRule[] = [];

    const selectedSourceType = widgetFilters?.sourceType ?? sourceTypes[0].id;

    try {
        selectedKeywords = JSON.parse(widgetFilters.multiIncludeExcludeKeywords);
        selectedWebsites = JSON.parse(widgetFilters.multiIncludeExcludeWebsites);
    } catch (e) {
        Sentry.error("Error parsing multiIncludeExclude filters", e);
    }

    const getSelectedFilters = () => {
        return Object.entries(widgetFilters).map(([name, value]) => ({ name, value }));
    };

    const handleClicksSourceTypeChange = ({ id }: { id: string }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: SOURCE_TYPE,
                value: id,
            },
        ]);
    };

    const handleMultiIncludeExcludeChange = (rules: IRule[], isKeywords: boolean) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: isKeywords ? "multiIncludeExcludeKeywords" : "multiIncludeExcludeWebsites",
                value: JSON.stringify(rules),
            },
        ]);
    };

    const handleClicksChangeChange = ({ id }: { id: EChangeFilterValues }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "Change",
                value: id,
            },
        ]);
    };

    const handleOrderByChange = ({ id }: { id: string }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "orderBy",
                value: id,
            },
        ]);
    };

    return (
        <div className="filters">
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.orderBy</I18n>
                </h5>
                <StatefulDropdown
                    items={orderByOptions}
                    selectedId={widgetFilters.orderBy ?? orderByOptions[0].id}
                    onSelect={handleOrderByChange}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.clicksChange</I18n>
                </h5>
                <StatefulDropdown
                    items={getChangeFilterItemsWithClear()}
                    selectedId={widgetFilters.Change}
                    onSelect={handleClicksChangeChange}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.trafficType</I18n>
                </h5>
                <StatefulDropdown
                    items={sourceTypes}
                    selectedId={selectedSourceType}
                    onSelect={handleClicksSourceTypeChange}
                />
            </div>
            <div
                className="dynamicFilter"
                style={{ width: "100%" }}
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.multiIncludeExcludeKeywords</I18n>
                </h5>
                <MultiIncludeExcludeKeywordsInline
                    rules={selectedKeywords}
                    onMultiIncludeExcludeChange={(rules) => handleMultiIncludeExcludeChange(rules, true)}
                />
            </div>
            <div
                className="dynamicFilter"
                style={{ width: "100%" }}
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.multiIncludeExcludeWebsites</I18n>
                </h5>
                <MultiIncludeExcludeWebsitesInline
                    rules={selectedWebsites}
                    onMultiIncludeExcludeChange={(rules) => handleMultiIncludeExcludeChange(rules, false)}
                />
            </div>
        </div>
    );
};
